import React, { useState, useEffect } from "react";
import { FundraiserResponse } from "../../Interfaces/Fundraiser";
import config from "../../config";
import "./FundraisersAwaitingCoordinator.css"; // Import your CSS file
import fetchWithAuth from "../../services/fetchWithAuth";

const FundraisersAwaitingCoordinator = ({ user_id }: { user_id: number }) => {
  const [awaitingFundraisers, setAwaitingFundraisers] = useState<
    FundraiserResponse[]
  >([]);
  const [showNotice, setShowNotice] = useState(false);

  useEffect(() => {
    const fetchFundraisers = async () => {
      try {
        const response = await fetchWithAuth(
          `${config.API_BASE_URL}users/${user_id}/pending-fundraisers/`
        );
        const data: FundraiserResponse[] = await response.json();

        if (data.length === 0) return; // If initially empty, component remains null
        setAwaitingFundraisers(data);
      } catch (error) {
        console.log("Failed to fetch fundraisers", error);
      }
    };

    fetchFundraisers();
  }, [user_id]);

  // Function to handle PUT request and update fundraiser's coordinator
  const handleCoordinatorUpdate = async (fundraiserId: number) => {
    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}fundraisers/${fundraiserId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ coordinator: user_id }),
        }
      );

      if (response.ok) {
        const updatedFundraiser: FundraiserResponse = await response.json();

        setAwaitingFundraisers((prevFundraisers) => {
          const updatedList = prevFundraisers.filter(
            (fundraiser) => fundraiser.id !== fundraiserId
          );

          setShowNotice(true);

          return updatedList;
        });
      } else {
        console.error("Failed to update coordinator for fundraiser");
      }
    } catch (error) {
      console.log("Error making PUT request", error);
    }
  };

  // If the list is initially empty, return null
  if (awaitingFundraisers.length === 0 && !showNotice) {
    return null;
  }

  return (
    <div className="fundraisers-awaiting-coordinator-container">
      {/* Title Section */}
      <div className="fundraisers-awaiting-coordinator-title">
        <h2>Fundraisers awaiting your coordinator confirmation</h2>
      </div>

      {/* Disclaimer Section */}
      <div className="fundraisers-awaiting-coordinator-disclaimer">
        <p>
          <strong>Disclaimer:</strong> By clicking "Accept," you acknowledge
          that you are the fundraiser coordinator for this fundraiser and take
          on the associated responsibilities.
        </p>
      </div>

      <table className="fundraisers-awaiting-coordinator-table">
        <thead>
          <tr>
            <th>Organization Name</th>
            <th>Fundraiser Name</th>
            <th>Fundraiser Join Code</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {awaitingFundraisers.map((fundraiser) => (
            <tr key={fundraiser.id}>
              <td data-label="Organization Name">{fundraiser.org_name}</td>
              <td data-label="Fundraiser Name">{fundraiser.fundraiser_name}</td>
              <td data-label="Join Code">{fundraiser.fundraiser_code}</td>
              <td data-label="Start Date">
                {new Date(fundraiser.start_date).toLocaleDateString()}
              </td>
              <td data-label="End Date">
                {new Date(fundraiser.end_date).toLocaleDateString()}
              </td>
              <td data-label="Action">
                <button
                  className="fundraisers-awaiting-coordinator-button"
                  onClick={() => handleCoordinatorUpdate(fundraiser.id)}
                >
                  Accept
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showNotice && (
        <div className="fundraisers-awaiting-coordinator-notice">
          <p>
            The fundraiser(s) will display on your dashboard after you refresh
            the page.
          </p>
        </div>
      )}
    </div>
  );
};

export default FundraisersAwaitingCoordinator;
